import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Paginated } from '@insurensa/shared';
import { Badge, Card, DataTable, PageTitle, type DataTableColumn } from '@insurensa/ui';
import { useFormat, useT } from '@insurensa/i18n';
import { api, qs } from '../lib/api.js';
import { ErrorAlert } from '../components/ui.js';

interface AuditRow {
  id: string;
  actorType: string;
  actorId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

/**
 * The audit trail. There was no equivalent in the legacy console — approvals,
 * deletions and moderation decisions left no record of who made them.
 */
export function AuditLogPage(): JSX.Element {
  const t = useT();
  const fmt = useFormat();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');

  const { data, error, isLoading } = useQuery({
    queryKey: ['audit-logs', entityType, action, page, pageSize],
    queryFn: () =>
      api.get<Paginated<AuditRow>>(`/admin/audit-logs${qs({ entityType, action, page, pageSize })}`),
  });

  const columns: Array<DataTableColumn<AuditRow>> = [
    {
      key: 'createdAt',
      header: t('admin.auditLog.timestamp'),
      value: (row) => fmt.dateTime(row.createdAt),
    },
    { key: 'action', header: t('admin.auditLog.action'), value: (row) => row.action },
    {
      key: 'entityType',
      header: t('admin.auditLog.entity'),
      value: (row) => `${row.entityType}${row.entityId ? ` ${row.entityId.slice(0, 8)}` : ''}`,
      render: (row) => (
        <>
          {row.entityType}
          {row.entityId && <div className="small text-muted">{row.entityId.slice(0, 8)}</div>}
        </>
      ),
    },
    {
      key: 'actorType',
      header: t('admin.auditLog.performedBy'),
      value: (row) => row.actorType,
      render: (row) => <Badge tone="secondary">{row.actorType}</Badge>,
    },
    { key: 'ipAddress', header: t('admin.auditLog.ip'), value: (row) => row.ipAddress ?? fmt.empty },
  ];

  return (
    <>
      <PageTitle
        heading={t('admin.auditLog.heading')}
        subheading={t('admin.auditLog.subheading')}
        icon="pe-7s-safe"
      />

      <ErrorAlert error={error} />

      <Card noPadding>
        <div className="card-body">
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            loading={isLoading}
            exportFileName="audit-log"
            exportTitle={t('admin.auditLog.heading')}
            emptyMessage={t('admin.auditLog.empty')}
            search={{
              value: action,
              onChange: (value) => {
                setAction(value);
                setPage(1);
              },
            }}
            pagination={{
              page: data?.page ?? page,
              pageSize: data?.pageSize ?? pageSize,
              total: data?.total ?? 0,
              totalPages: data?.totalPages ?? 1,
              onPage: setPage,
              onPageSize: (next) => {
                setPageSize(next);
                setPage(1);
              },
            }}
            toolbar={
              <div className="form-inline mt-2">
                <label className="mr-2 mb-0 small text-muted" htmlFor="entityType">
                  {t('admin.auditLog.entity')}
                </label>
                <select
                  id="entityType"
                  className="form-control form-control-sm"
                  value={entityType}
                  onChange={(event) => {
                    setEntityType(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t('common.all')}</option>
                  <option value="Partner">{t('admin.auditLog.entityPartner')}</option>
                  <option value="User">{t('admin.auditLog.entityUser')}</option>
                  <option value="Review">{t('admin.auditLog.entityReview')}</option>
                  <option value="Offer">{t('admin.auditLog.entityOffer')}</option>
                  <option value="AdminUser">{t('admin.auditLog.entityAdmin')}</option>
                  <option value="AppRelease">{t('admin.auditLog.entityAppRelease')}</option>
                </select>
              </div>
            }
          />
        </div>
      </Card>
    </>
  );
}
